// Port of SuiteModel/Travel.swift (`TravelSettings`, `Travel`) and the formatting the journeys use (Continuity's
// `distanceText`/`durationText`, the app's `TravelFormat`): how people get about in the story — today's transport (cars
// and aeroplanes; on the Mac and iPad also Apple Maps' routes), or the modes of the story's period, each at a steady
// average speed (on foot, a horse-drawn cab, a steam train). Cast & Places shows journeys between places in both;
// continuity's travel check asks whether a character could make a journey in the time between two scenes.
//
// Stored as JSON at `settings.travel` (through the settings-JSON rules), and only for a story set in the past: today's
// transport stores nothing.
import type { StoryEntity } from '../model/documents.ts';
import { makeID } from '../edit/editProject.ts';
import { decodeJSON, type JSONDecode } from '../settingsJSON.ts';

/** `TravelSettings.Mode`: a way of travelling at its average speed over a whole journey, stops included. */
export interface TravelMode {
  id: string;
  name: string;
  kmh: number;
}

/** `TravelSettings`: the story's own ways of travelling; empty when it's set today. */
export interface TravelSettings {
  periodModes: TravelMode[];
}

/** `SettingsKey.travel`. */
export const TRAVEL_KEY = 'travel';

/** `TravelSettings()`: today's transport. */
export const todayTravel = (): TravelSettings => ({ periodModes: [] });

/** `TravelSettings.isPeriod`: whether journeys go at the period's speeds. */
export const isPeriodTravel = (t: TravelSettings): boolean => t.periodModes.some((m) => m.kmh > 0);

/** `TravelSettings.suggestions`: modes to add from, with typical average speeds. */
export const TRAVEL_SUGGESTIONS: readonly { name: string; kmh: number }[] = [
  { name: 'On foot', kmh: 5 }, { name: 'On horseback', kmh: 10 }, { name: 'Horse-drawn cab or carriage', kmh: 12 }, { name: 'Stagecoach', kmh: 10 },
  { name: 'Bicycle', kmh: 15 }, { name: 'Steam train', kmh: 50 }, { name: 'Sailing ship', kmh: 9 }, { name: 'Motor car', kmh: 40 },
];

/** A new mode, as `Mode(name:kmh:)` makes it (a fresh id). */
export const travelMode = (name: string, kmh: number, id: string = makeID()): TravelMode => ({ id, name, kmh });

/** `TravelSettings.beforeCars`: what "by the story's period" starts with. */
export const beforeCars = (id: () => string = makeID): TravelSettings => ({
  periodModes: [travelMode('On foot', 5, id()), travelMode('Horse-drawn cab or carriage', 12, id())],
});

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** `Mode`'s synthesized `Decodable`: every field present and of its type (a number for `kmh`). */
function decodeMode(json: unknown): TravelMode | null {
  if (!isObject(json)) return null;
  const { id, name, kmh } = json;
  if (typeof id !== 'string' || typeof name !== 'string' || typeof kmh !== 'number' || !Number.isFinite(kmh)) return null;
  return { id, name, kmh };
}

/** `TravelSettings.init(from:)`: an object (else unreadable); `periodModes` read whole — one mode that can't be read
 *  empties the list, as `try? decodeIfPresent([Mode].self)` does — and keys added later ignored. */
export const decodeTravelSettings: JSONDecode<TravelSettings> = (json) => {
  if (!isObject(json)) return null;
  const raw = json.periodModes;
  if (!Array.isArray(raw)) return { periodModes: [] };
  const modes = raw.map(decodeMode);
  return { periodModes: modes.every((m) => m !== null) ? (modes as TravelMode[]) : [] };
};

/** `ProjectDocument.travelSettings()`: how people travel in the story; today's transport when never set (or unreadable). */
export const readTravel = (settings: Record<string, unknown>): TravelSettings => decodeJSON(settings[TRAVEL_KEY], decodeTravelSettings) ?? todayTravel();

/** Whether two settings say the same (`==`). */
export const sameTravel = (a: TravelSettings, b: TravelSettings): boolean =>
  a.periodModes.length === b.periodModes.length && a.periodModes.every((m, i) => {
    const o = b.periodModes[i];
    return m.id === o.id && m.name === o.name && m.kmh === o.kmh;
  });

// MARK: Travel

/** `Travel.distance(latitude:longitude:toLatitude:longitude:)`: great-circle distance in metres (haversine on a sphere of
 *  the Earth's mean radius). */
export function travelDistance(a: number, b: number, c: number, d: number): number {
  const r = 6_371_008.8, rad = Math.PI / 180;
  const dLat = (c - a) * rad, dLon = (d - b) * rad;
  const h = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(a * rad) * Math.cos(c * rad) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return 2 * r * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** `Travel.distance(_:_:)`: the straight line between two places, when both are on the map. */
export function placeDistance(a: Pick<StoryEntity, 'latitude' | 'longitude'>, b: Pick<StoryEntity, 'latitude' | 'longitude'>): number | null {
  if (a.latitude == null || a.longitude == null || b.latitude == null || b.longitude == null) return null;
  return travelDistance(a.latitude, a.longitude, b.latitude, b.longitude);
}

/** `Travel.detour`: roads and paths wind; a route is usually about this much longer than the straight line. */
export const DETOUR = 1.3;

/** `Travel.phrase`: a mode's name as it follows a time ("On foot" → "on foot", "Hansom cab" → "by hansom cab"). */
export function travelPhrase(name: string): string {
  // `trimmingCharacters(in: .whitespaces)`: spaces and tabs, not newlines.
  const lower = name.replace(/^[\t\p{Zs}]+|[\t\p{Zs}]+$/gu, '').toLowerCase();
  return ['on ', 'by ', 'in ', 'aboard '].some((p) => lower.startsWith(p)) ? lower : 'by ' + lower;
}

/** `Travel.time(metres:kmh:)`: seconds for a route of `metres` at `kmh` (forever at no speed). */
export const travelTime = (metres: number, kmh: number): number => (kmh > 0 ? (metres / 1000 / kmh) * 3600 : Infinity);

/** `Travel.shortest(straight:travel:)`: the least a journey between places `straight` metres apart can take (seconds),
 *  and how ("on foot", "by hansom cab"): the story's fastest mode over the usual detour, or today by road (100 km/h at
 *  best) or by air (800 km/h, plus three hours at the airports). */
export function shortestJourney(straight: number, travel: TravelSettings): { time: number; mode: string } {
  const route = straight * DETOUR;
  if (isPeriodTravel(travel)) {
    // `max(by:)`: the first of the fastest.
    let fastest: TravelMode | null = null;
    for (const m of travel.periodModes) if (m.kmh > 0 && (fastest === null || fastest.kmh < m.kmh)) fastest = m;
    if (fastest) return { time: travelTime(route, fastest.kmh), mode: travelPhrase(fastest.name) };
  }
  const road = travelTime(route, 100), air = 3 * 3600 + travelTime(straight, 800);
  return road <= air ? { time: road, mode: 'by road' } : { time: air, mode: 'by air' };
}

// MARK: Formatting

type NumberOptions = Intl.NumberFormatOptions & { roundingPriority?: string; roundingMode?: string; roundingIncrement?: number };
const formatters = new Map<string, Intl.NumberFormat>();
function numberFormat(locale: string, options: NumberOptions): Intl.NumberFormat {
  const key = locale + JSON.stringify(options);
  let f = formatters.get(key);
  if (!f) formatters.set(key, (f = new Intl.NumberFormat(locale, options as Intl.NumberFormatOptions)));
  return f;
}

/** The region whose measurements `locale` follows, as ICU finds it: a measurement system asked for (`-u-ms-metric`,
 *  `ussystem`, `uksystem`), else a region override (`-u-rg-gbzzzz`), else the locale's region (likely, when it names
 *  none: "en" is the United States). "001" is the world's default. */
export function measurementRegion(locale: string): string {
  const ext = /-u((?:-[a-z0-9]{2,8})*)$/i.exec(locale.replace(/_/g, '-'))?.[1]?.toLowerCase() ?? '';
  const keyword = (k: string) => new RegExp(`-${k}-([a-z0-9]+)`).exec(ext)?.[1];
  const ms = keyword('ms');
  if (ms === 'metric') return '001';
  if (ms === 'ussystem') return 'US';
  if (ms === 'uksystem') return 'GB';
  const rg = keyword('rg');
  if (rg && /^[a-z]{2}zzzz$/.test(rg)) return rg.slice(0, 2).toUpperCase();
  try {
    return new Intl.Locale(locale).maximize().region ?? '001';
  } catch {
    return '001';
  }
}

/** `Locale.measurementSystem != .metric`: speeds in miles an hour (the United States, Liberia, Myanmar and Britain). */
export const usesMiles = (locale: string): boolean => ['US', 'LR', 'MM', 'GB'].includes(measurementRegion(locale));

/** Miles in a kilometre, as the travel sheet converts speeds. */
export const MILES_PER_KM = 0.621371;

/** Round half to even at a multiple of `step`, as ICU's increments do. */
function roundToIncrement(value: number, step: number): number {
  const q = value / step, floor = Math.floor(q), diff = q - floor;
  const n = Math.abs(diff - 0.5) < 1e-9 ? (floor % 2 === 0 ? floor : floor + 1) : Math.round(q);
  return n * step;
}

/**
 * `Measurement(value:unit: .meters).formatted(.measurement(width: .abbreviated, usage: .road))`: "1.2 km" or "0.8 mi",
 * as the locale's region measures roads (CLDR's unit preferences for road lengths, which ICU follows): miles in the
 * United States and Britain (feet, or yards in Britain, under half a mile), kilometres elsewhere (metres under 0.9 km);
 * short lengths to the nearest 50 (from 300 metres or 100 feet or yards) or 10, the others to whole units but at least two
 * significant digits ("1.2 km", "12 km").
 */
export function distanceText(metres: number, locale = 'en-US'): string {
  const region = measurementRegion(locale);
  const imperial = region === 'US' || region === 'GB';
  const short = region === 'US' ? 'foot' : 'yard';
  let unit: string, value: number;
  if (imperial) {
    const miles = metres / 1609.344;
    if (Math.abs(miles) * (1 + Number.EPSILON) >= 0.5) {
      unit = 'mile';
      value = miles;
    } else {
      unit = short;
      value = short === 'foot' ? metres / 0.3048 : metres / 0.9144;
    }
  } else {
    const km = metres / 1000;
    if (Math.abs(km) * (1 + Number.EPSILON) >= 0.9) {
      unit = 'kilometer';
      value = km;
    } else {
      unit = 'meter';
      value = metres;
    }
  }
  const large = unit === 'mile' || unit === 'kilometer';
  if (large) {
    // ICU's default for a usage without its own precision: `Precision::integer().withMinDigits(2)`.
    return numberFormat(locale, {
      style: 'unit', unit, unitDisplay: 'short', maximumFractionDigits: 0, maximumSignificantDigits: 2, roundingPriority: 'morePrecision', roundingMode: 'halfEven',
    }).format(value);
  }
  const abs = Math.abs(value) * (1 + Number.EPSILON);
  const step = abs >= (unit === 'meter' ? 300 : 100) ? 50 : abs >= 10 ? 10 : 1;
  return numberFormat(locale, { style: 'unit', unit, unitDisplay: 'short', maximumFractionDigits: 0 }).format(roundToIncrement(value, step));
}

/** Days, hours and minutes in `width` ("2 hours, 36 minutes"; "2 hr, 5 min"), as `Duration.UnitsFormatStyle` with
 *  `allowed: [.days, .hours, .minutes]` writes whole minutes: units at zero left out, all at zero "0 minutes". */
function unitsText(minutes: number, width: 'long' | 'short', locale: string): string {
  const days = Math.floor(minutes / 1440), hours = Math.floor((minutes % 1440) / 60), mins = minutes % 60;
  const parts: [number, string][] = ([[days, 'day'], [hours, 'hour'], [mins, 'minute']] as [number, string][]).filter(([n]) => n > 0);
  if (!parts.length) parts.push([0, 'minute']);
  const texts = parts.map(([n, unit]) => numberFormat(locale, { style: 'unit', unit, unitDisplay: width }).format(n));
  return new Intl.ListFormat(locale, { type: 'unit', style: width }).format(texts);
}

/** `Continuity.durationText`: "25 minutes", "3 hours, 10 minutes", rounded up to the minute. */
export const durationText = (seconds: number, locale = 'en-US'): string => unitsText(Math.ceil(seconds / 60), 'long', locale);

/** `TravelFormat.duration` (Cast & Places): "18 min", "2 hr, 5 min", "3 days, 4 hr", to the nearest minute (at least
 *  one); "—" when it would never arrive. */
export function journeyDuration(seconds: number, locale = 'en-US'): string {
  if (!Number.isFinite(seconds)) return '—';
  return unitsText(Math.max(1, Math.round(seconds / 60)), 'short', locale);
}
